import { useReveal } from '@/hooks/useReveal';

const campaignFeatures = [
  {
    icon: 'ri-message-3-line',
    title: 'Rich Message Templates',
    description: 'Pre-approved WhatsApp templates for promotional, transactional, and service messages. Get 99% delivery rate.',
  },
  {
    icon: 'ri-image-line',
    title: 'Images & PDF Catalogs',
    description: 'Share product images, brochures, price lists, and catalogs directly in WhatsApp chats with one click.',
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'Interactive Buttons',
    description: 'Quick reply buttons and call-to-action buttons that drive 3x more engagement than plain text.',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Product Catalogs & Payments',
    description: 'Share your entire product catalog with in-chat payment links. Customers can browse and buy without leaving WhatsApp.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Location Sharing',
    description: 'Send store locations, service areas, and meeting points directly in conversations.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Campaign Analytics',
    description: 'Real-time delivery reports, read rates, click-through rates, and conversion tracking for every campaign.',
  },
];

const benefits = [
  { icon: 'ri-speed-up-line', label: '10x Faster Outreach', value: 'Reach 10,000 customers in minutes, not days' },
  { icon: 'ri-percent-line', label: '98% Open Rate', value: 'WhatsApp messages have the highest open rate of any channel' },
  { icon: 'ri-money-dollar-circle-line', label: '5x ROI', value: 'Average businesses see 5x return on their WhatsApp marketing spend' },
  { icon: 'ri-user-heart-line', label: 'Personalized at Scale', value: 'Segment customers and send personalized messages automatically' },
];

export default function BulkMessagingSection() {
  const { ref, visible } = useReveal();

  return (
    <section id="features" className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
            WhatsApp Bulk Messaging
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Reach Thousands.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              Feel Personal.
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Send bulk WhatsApp messages that don&apos;t feel like spam. Rich media, smart segmentation,
            and real-time analytics — all through the official WhatsApp Business API.
          </p>
        </div>

        {/* Campaign Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-16">
          {campaignFeatures.map(feature => (
            <div
              key={feature.title}
              className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:border-accent-200/50 transition-all duration-400 hover:-translate-y-1"
            >
              <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center mb-4 group-hover:bg-accent-200 transition-colors">
                <i className={`${feature.icon} text-lg text-accent-600`}></i>
              </div>
              <h3 className="text-base font-bold font-heading text-foreground-950 mb-2">{feature.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Benefits Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {benefits.map(benefit => (
            <div key={benefit.label} className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/50">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className={`${benefit.icon} text-lg text-primary-600`}></i>
              </div>
              <p className="text-sm font-bold font-heading text-foreground-950 mb-1">{benefit.label}</p>
              <p className="text-xs text-foreground-500 leading-relaxed">{benefit.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}